import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Save, X, AlertCircle, ArrowLeft, Upload, FileText, Trash2 } from 'lucide-react';
import { DownloadFormData } from '../../types/downloads.types';
import { toast } from 'react-toastify';
import { downloadsService } from '../../services/api/downloadsService';

const PUBLIC_SITE = 'https://pred.telangana.gov.in';

export function resolveDownloadDocUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return url;
  return `${PUBLIC_SITE}/${url}`;
}

const DownloadsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<DownloadFormData>({
    title: '',
    description: '',
    document_url: '',
    category: '',
    is_active: true,
    display_order: 0,
  });

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditMode && id) fetchDownload(parseInt(id));
  }, [id, isEditMode]);

  const fetchDownload = async (downloadId: number) => {
    try {
      setFetchingData(true);
      const response = await downloadsService.getById(downloadId);
      if (response.success) {
        const d = response.data;
        setFormData({
          title: d.title || '',
          description: d.description || '',
          document_url: d.document_url || '',
          category: d.category || '',
          is_active: d.is_active,
          display_order: d.display_order || 0,
        });
      }
    } catch {
      toast.error('Failed to load download');
      navigate('/downloads');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowed.includes(file.type)) {
      toast.error('Only PDF, Excel, and Word files are allowed');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be less than 20MB');
      return;
    }

    try {
      setUploading(true);
      const response = await downloadsService.uploadFile(file);
      if (response.success && response.data) {
        const url = response.data.url;
        const relativePath = url.includes('/uploads/') ? '/uploads/' + url.split('/uploads/')[1] : url;
        setFormData((prev) => ({ ...prev, document_url: relativePath }));
        setErrors((prev) => ({ ...prev, document_url: '' }));
        toast.success('File uploaded');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!formData.title.trim()) errs.title = 'Title is required';
    if (!formData.document_url.trim()) errs.document_url = 'Please upload a file';
    if (formData.display_order < 0) errs.display_order = 'Must be 0 or greater';
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error('Please fix errors'); return; }

    try {
      setLoading(true);
      if (isEditMode && id) {
        await downloadsService.update(parseInt(id), formData);
        toast.success('Download updated');
      } else {
        await downloadsService.create(formData);
        toast.success('Download created');
      }
      navigate('/downloads');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Download' : 'Add Download'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode ? 'Update download details' : 'Add a new downloadable file'}
          </p>
        </div>
        <Link to="/downloads" className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <ArrowLeft size={18} className="mr-2" /> Back to List
        </Link>
      </div>

      <div className="bg-white shadow-sm border border-gray-200 rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
            <input type="text" name="title" value={formData.title} onChange={handleChange}
              className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${errors.title ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`}
              placeholder="e.g., Annual Report 2025" />
            {errors.title && <p className="mt-1 text-sm text-red-600"><AlertCircle size={14} className="inline mr-1" />{errors.title}</p>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <input type="text" name="category" value={formData.category} onChange={handleChange}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Reports, Forms, Circulars" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={3}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Brief description of the download" />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">File <span className="text-red-500">*</span></label>

            {formData.document_url ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <FileText size={24} className="text-red-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <a href={resolveDownloadDocUrl(formData.document_url)} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline truncate block">
                    {formData.document_url.split('/').pop()}
                  </a>
                  <p className="text-xs text-gray-500 mt-0.5">Click to preview</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="text-xs text-blue-600 hover:text-blue-800">Change</button>
                  <button type="button" onClick={() => setFormData((prev) => ({ ...prev, document_url: '' }))}
                    className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            ) : (
              <div onClick={() => !uploading && fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  errors.document_url ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}>
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload size={28} className="text-gray-400" />
                    <p className="text-sm text-gray-600">Click to upload a file</p>
                    <p className="text-xs text-gray-400">PDF, Excel, Word. Max 20MB</p>
                  </div>
                )}
              </div>
            )}

            <input ref={fileInputRef} type="file" accept=".pdf,.xls,.xlsx,.doc,.docx" onChange={handleFileSelect} className="hidden" />
            {errors.document_url && <p className="mt-1 text-sm text-red-600"><AlertCircle size={14} className="inline mr-1" />{errors.document_url}</p>}
          </div>

          {/* Display Order & Active */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" name="display_order" value={formData.display_order} onChange={handleChange} min="0"
                className={`block w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${errors.display_order ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`} />
              {errors.display_order && <p className="mt-1 text-sm text-red-600">{errors.display_order}</p>}
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center cursor-pointer">
                <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
              </label>
              <span className="ml-2 text-sm text-gray-500">(Visible on website)</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button type="button" onClick={() => navigate('/downloads')} disabled={loading}
              className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <X size={18} className="mr-2" /> Cancel
            </button>
            <button type="submit" disabled={loading || uploading}
              className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>{isEditMode ? 'Updating...' : 'Creating...'}</>
              ) : (
                <><Save size={18} className="mr-2" />{isEditMode ? 'Update' : 'Create'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DownloadsForm;
